import React, { useEffect, useLayoutEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { nextStep, setFormData } from '../../redux/slice/findJobSlice';
import { useForm } from 'react-hook-form';
import { fetchSpecialization } from '../../redux/action/commonAction';
import Button from '../../components/general/Button';
import { SENIORITY_LEVEL } from '../../helpers/constants';
import Image from '../../components/general/Image';
import Select from '../../components/general/Select';
import { toast } from 'react-toastify';
import { ArrowUpRight } from '../../components/common/Icons';

const StepThreeForm = () => {
    const dispatch = useDispatch();
    const specialization = useSelector(state => state.findJob.stepThreeData.specialization);
    const seniority = useSelector(state => state.findJob.stepThreeData.seniority);
    const { isLoading, specializationList } = useSelector(state => state.common.spec);

    const { control, reset, watch, handleSubmit } = useForm({
        defaultValues: { seniority }
    });

    useLayoutEffect(() => {
        if (!isLoading) return;
        dispatch(fetchSpecialization())
        // eslint-disable-next-line
    }, [isLoading])

    useEffect(() => {
        reset({ seniority })
        // eslint-disable-next-line
    }, [])

    const seniorityOptions = useMemo(() => Object.keys(SENIORITY_LEVEL).map((key) => ({
        label: SENIORITY_LEVEL[key].displayValue,
        value: key
    })), [SENIORITY_LEVEL])

    const handleSelect = (value) => {
        dispatch(setFormData({ formData: { specialization: value } }))
    }

    const handleFormSubmit = () => {
        if (!specialization || !seniority) {
            toast.error("Please select specialization and seniority")
            return;
        };

        handleSubmit((data) => {
            dispatch(setFormData({ formData: data }));
            dispatch(nextStep())
        })()
    }

    return (
        <div className='min-h-[60vh] !py-[50px] !pb-[70px] lg:flex lg:justify-between lg:gap-[30px]'>
            <div className='lg:w-[40%]'>
                <div>
                    <p className='text-primary-color'> 3/8 </p>
                    <h1 className="text-[1.6rem] font-semibold">
                        <span className='lg:block'> What is your main </span>
                        {/* <Image src={DoubleStar} alt="double-star" className="hidden lg:inline-block" /> */}
                        <span className='lg:block'>specialization?</span>
                    </h1>
                </div>

                <div className='hidden lg:block mt-[20px] text-[#2A2A2A]'>
                    <div className='mt-[20px] text-[#2A2A2A]'>
                        <p> 4/8 </p>
                        <h1 className="text-[1.6rem] font-semibold text-[#2A2A2A]">
                            <span> When did your </span>
                            <span className='lg:block'>commercial experience start?</span>
                        </h1>
                    </div>
                    <div className='mt-[20px]'>
                        <p> 5/8 </p>
                        <h1 className="text-[1.6rem] font-semibold">
                            <span> Choose your main </span>
                            <span className='lg:block'> technologies?</span>
                        </h1>
                    </div>
                </div>
            </div>

            <div className='lg:w-[60%]'>
                <div className='hidden lg:flex gap-[20px] text-[13px] mb-[50px] lg:justify-center'>
                    <p className='text-primary-color'> 1. Project Preferences </p>
                    <p className='text-primary-color'> 2. Specialization in stack </p>
                    <p> 3. Additional info </p>
                    <p> 4. Registration </p>
                </div>

                <div className='text-[15px] mb-[30px]'>
                    <div className='flex items-center justify-center gap-[10px] text-[#797979]'>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 hidden lg:block">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                        </svg>

                        <div className='text-left lg:text-center'>
                            Here we expect to see your major specialization, but you can add some extra
                        </div>
                    </div>
                    <div className='text-left lg:text-center text-[#797979]'>
                        ones later in your profile
                    </div>
                </div>

                <div className='flex flex-col md:grid md:grid-cols-2 lg:grid-cols-3 gap-[20px] mt-[20px] lg:max-h-[230px] lg:overflow-y-auto'>
                    {
                        specializationList.map((spec, index) => (
                            <div key={index} className={`bg-[#292929] rounded-[6px] flex items-center gap-4 justify-center p-[10px] lg:p-[20px] cursor-pointer ${specialization === spec.id ? 'border border-primary-color bg-black' : ''}`} onClick={() => handleSelect(spec.id)}>
                                {
                                    spec.icon_details ? 
                                    <Image src={spec.icon_details.file} alt={'.'} className='w-[30px] h-[30px]' />
                                    :
                                    <div className='w-[30px] h-[30px] flex items-center justify-center'>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 text-white">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
                                        </svg>
                                    </div>
                                }

                                <div>
                                    <h3 className='text-[18px] lg:text-[1rem]'>{spec.name}</h3>
                                </div>
                            </div>
                        ))
                    }
                </div>

                
                <div className='mt-[20px]'>
                    <label className='block text-primary-color mb-[10px]'> Seniority Level </label>
                    <Select 
                        options={seniorityOptions}
                        defaultValue={{
                            label: SENIORITY_LEVEL['INTERMEDIATE'].displayValue,
                            value: 'INTERMEDIATE'
                        }}
                        onChange={(value) => {
                            dispatch(setFormData({ formData: { seniority: value } }))
                        }}
                    />
                </div>
                
                <Button disabled={!Boolean(specialization) || !Boolean(watch("seniority"))} onClick={handleFormSubmit} className='w-full bg-primary-color text-black h-[40px] lg:h-[50px] text-[18px] lg:text-[20px] lg:mx-auto rounded mt-[20px] md:w-[200px] lg:mt-[50px]'>
                  <div className="flex items-center justify-center gap-2">
                    <span>Ok, next</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Button>
            </div>
        </div>
    )
}

export default StepThreeForm